import { useMemo } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { Link } from 'react-router-dom'
import { Anchor } from '@mantine/core'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'
import { PillAlert } from '@shared/components/pill-alert/pill-alert'
import { tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { SearchStationResult } from '@loom-api-types'

interface StationSearchTableProps {
  data?: SearchStationResult[]
  renderKey?: string
  noDataMessage?: string
  isFetching: boolean
  isLoading: boolean
  isError: boolean
}

export function StationsSearchTable({
  data = [],
  renderKey = '',
  noDataMessage = 'No stations found',
  isFetching,
  isLoading,
  isError,
}: StationSearchTableProps) {
  const columns = useMemo<MRT_ColumnDef<SearchStationResult>[]>(
    () => [
      {
        id: 'stationDetailLink',
        accessorKey: 'recordNumber',
        header: 'Station ID',
        Cell: ({ row }) => (
          <Anchor component={Link} to={`/stations/${row.original.recordNumber}/details`} size='sm'>
            {row.original.recordNumber}
          </Anchor>
        ),
      },
      {
        id: 'brand',
        accessorKey: 'brand',
        header: 'Brand',
      },
      {
        id: 'tradingName',
        accessorKey: 'tradingName',
        header: 'Trading Name',
      },
      {
        id: 'agentOrBuyer',
        accessorKey: 'agentOrBuyer',
        header: 'Area Manager',
      },
      {
        id: 'isOnHold',
        accessorKey: 'isOnHold',
        header: 'Status',
        Cell: ({ cell }) => (cell.getValue() ? <PillAlert text='On Hold' hideIcon /> : null),
      },
      {
        id: 'woolDataLink',
        accessorKey: 'recordNumber',
        header: '',
        Cell: ({ row }) => (
          <Anchor
            component={Link}
            to={`/stations/${row.original.recordNumber}/wool-data`}
            size='sm'
          >
            Wool Data
          </Anchor>
        ),
      },
    ],
    []
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    initialState: {
      ...tableDefaultProps.initialState,
      // Ensures the link columns are always displayed
      columnPinning: { left: ['stationDetailLink'], right: ['woolDataLink'] },
    },
    state: {
      showSkeletons: isLoading,
      showProgressBars: isFetching,
      showAlertBanner: isError,
    },
    enableTopToolbar: false,
    enableColumnPinning: true,
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
  })

  return isError ? (
    <IconAlert title='Error retrieving search results' level={AlertLevel.ERROR} />
  ) : (
    <MantineReactTable table={table} key={renderKey} />
  )
}
